<template>
  <div class="video-container">
    <video ref="videoPlayer" class="video-js"></video>
    <Overlay
      v-if="currentPart.endOfVideo"
      @showQuizModal="$emit('showQuizModal')"
      @play="restart"
      @exit="exitSession"
    />
  </div>
</template>

<script>
import Overlay from '@/components/shared/Overlay';
import videojs from 'video.js';
import VideoService from '@/services/api/video/video-service';
import { mapGetters } from 'vuex';
import 'videojs-seek-buttons';

export default {
  name: 'VideoPlayer',
  props: ['syncguid'],
  components: {
    Overlay,
  },
  data() {
    return {
      player: null,
      loading: true,
      options: {
        autoplay: false,
        controls: true,
        controlBar: {
          children: [
            { name: 'playToggle' },
            { name: 'seekButtons' },
            { name: 'currentTimeDisplay' },
            { name: 'timeDivider' },
            { name: 'durationDisplay' },
            { name: 'progressControl' },
            { name: 'captionsButton' },
            { name: 'audioTrackButton' },
            { name: 'volumePanel' },
            { name: 'fullscreenToggle' },
          ],
        },
        html5: {
          hls: {
            withCredentials: true,
            overrideNative: true,
          },
        },
        sources: [],
        plugins: {
          seekButtons: {
            back: 10,
          },
        },
        fluid: true,
      },

      currentPart: {
        status: 'i',
        score: 0,
        videoPosition: 0,
        maxVideoPosition: 0,
        watchedUntilEnd: false,
        endOfVideo: false,
      },

      currentTime: 0,
      timeInCourse: 0,
      trackingInterval: null,
      playing: false,
      seeking: false,
    };
  },
  computed: {
    ...mapGetters({ events: 'video/events' }),
    disableScrubbing() {
      const profile =
        this.$store.getters.contentById(this.syncguid).profile || {};
      return profile.disableScrubbing;
    },
    captions() {
      return this.$store.getters.contentById(this.syncguid).captions || [];
    },
    hasQuiz() {
      return (
        Object.keys(this.$store.getters.contentById(this.syncguid).quiz)
          .length > 0
      );
    },
    standard() {
      return this.$route.params.standard;
    },
    profile() {
      return this.$store.getters.contentById(this.syncguid).profile || {};
    },
    duration() {
      return this.player.duration();
    },
    userPreferredLanguage() {
      const language = localStorage.getItem('user-preferred-language') || navigator.language || '';
      if (language && !localStorage.getItem('user-preferred-language')) {
        localStorage.setItem('user-preferred-language', language);
      }
      return language;
    },
  },
  methods: {
    async getVideoSource() {
      const profile = this.profile;
      let parentSyncguid = profile.parentSyncguid;
      if (parentSyncguid === null || parentSyncguid === undefined) {
        parentSyncguid = this.syncguid;
      }
      return VideoService.getVideoSource(this.syncguid, parentSyncguid).then(
        source => {
          this.options.sources.push(source);
        },
      );
    },
    selectAudioTrackWithFallback() {
      const languageCode = this.userPreferredLanguage?.toLowerCase();
      const baseLanguage = languageCode?.split('-')[0]?.toLowerCase(); // Extract base language (e.g., "es")
      const audioTracks = Array.from(this.player.audioTracks());
      const selectedTrack = audioTracks.find(at => at.language.toLowerCase() === languageCode) || audioTracks.find(at => at.language.toLowerCase().startsWith(baseLanguage));
      if (selectedTrack) {
        audioTracks.forEach(at => {
          at.enabled = (at === selectedTrack);
        });
      }
    },
    saveUserPreferredLanguage() {
      const selectedTrack = Array.from(this.player.audioTracks()).find(track => track.enabled);
      if (selectedTrack) {
        localStorage.setItem('user-preferred-language', selectedTrack.language);
      }
    },
    seek(time) {
      this.$emit('play');
      this.player.currentTime(time);
      this.play();
    },
    pause() {
      if (this.player) {
        this.player.pause();
      }
    },
    play() {
      if (this.player) {
        this.player.play();
      }
    },
    restart() {
      if (this.player) {
        this.currentPart.endOfVideo = false;
        this.play();
      }
    },
    startTracking() {
      this.trackingInterval = setInterval(this.tick, 1000);
    },
    stopTracking() {
      clearInterval(this.trackingInterval);
    },
    tick() {
      this.stopTracking();
      this.timeInCourse += 1;
      if (
        this.timeInCourse >= 60 &&
        (this.currentPart.status === 'not-started' ||
          this.currentPart.status === '')
      ) {
        this.currentPart.status = 'i';
      }
      if (this.timeInCourse % 60 === 0) {
        this.saveProgress();
      }
      this.startTracking();
    },
    setupTextTracks(language) {
      const activeTrack = localStorage.getItem('caption-pref') || '';
      this.captions.forEach(c => {
        const track = this.player.addTextTrack('captions', c.label, c.language);
        track.mode = track.language === activeTrack ? 'showing' : 'disabled';

        c.cues.forEach(cue => {
          let currentId = 1;
          track.addCue({
            id: currentId,
            text: cue.text,
            startTime: cue.start,
            endTime: cue.end,
          });
          currentId++;
        });
      });
      this.player.textTracks().onchange = () => {
        const active = this.player
          .textTracks()
          .tracks_.find(tt => tt.mode === 'showing');
        if (!active || active.language === '') {
          localStorage.removeItem('caption-pref');
        } else {
          localStorage.setItem('caption-pref', active.language);
        }
      };
    },
    setupAudioTracks() {
      this.selectAudioTrackWithFallback();
      this.player.audioTracks().on('change', () => {
        this.saveUserPreferredLanguage();
      });
    },
    convertDataType(val) {
      if (val === 'undefined') return false;
      if (val === 'null') return null;
      if (val === 'false') return false;
      if (val === 'true') return true;
      if (!isNaN(parseInt(val))) return parseInt(val);
    },
    convertTime(val) {
      if (val) {
        const time = val.split(':');
        let s = 0;
        let m = 1;
        while (time.length > 0) {
          s += m * parseInt(time.pop(), 10);
          m *= 60;
        }
        return s;
      }
      return 0;
    },
    submitQuiz(quiz) {
      this.currentPart.status = quiz.passed ? 'p' : 'f';
      this.currentPart.score = quiz.score;
      this.saveProgress();
    },
    setup() {
      this.player = videojs(this.$refs.videoPlayer, this.options, () => {
        this.pause();
        this.player.volume(localStorage.getItem('volume-level') || 0.5);

        /** PLAYER EVENTS **/
        this.player.on('play', () => {
          this.playing = true;
          this.startTracking();
        });

        this.player.on('pause', () => {
          this.playing = false;
          this.stopTracking();
        });

        this.player.on('ended', () => {
          this.stopTracking();
          if (this.player.isFullscreen()) {
            this.player.exitFullscreen();
          }
          this.currentPart.endOfVideo = true;
          this.currentPart.watchedUntilEnd = true;
          if (!this.hasQuiz) {
            this.currentPart.status = 'c';
            this.currentPart.score = 100;
            if (this.standard !== 'AICC') {
              this.complete();
            }
          }
          this.saveProgress();
        });

        this.player.on('volumechange', () => {
          localStorage.setItem(
            'volume-level',
            this.player.muted() ? 0 : this.player.volume(),
          );
        });

        this.player.on('seeking', () => {
          this.seeking = true;
          if (this.disableScrubbing) {
            if (
              this.player.currentTime() <= this.currentPart.maxVideoPosition
            ) {
              this.currentPart.endOfVideo = false;
            } else {
              this.player.currentTime(this.currentTime);
            }
          }
        });

        this.player.on('seeked', () => {
          this.seeking = false;
        });

        this.player.on('timeupdate', () => {
          if (
            (!this.seeking && !this.player.paused()) ||
            !this.disableScrubbing ||
            this.player.currentTime() <= this.currentPart.maxVideoPosition
          ) {
            this.currentTime = this.player.currentTime();
          }

          if (this.player.currentTime() !== 0 || !this.loading) {
            this.loading = true;
            this.currentPart.videoPosition = this.player.currentTime();
            if (!this.disableScrubbing || !this.seeking) {
              this.currentPart.maxVideoPosition =
                this.currentPart.maxVideoPosition <= this.currentTime
                  ? this.currentTime
                  : this.currentPart.maxVideoPosition;
            }
          }
        });

        this.player.on('loadedmetadata', () => {
          if (!this.loading) {
            return;
          }
          this.setupTextTracks();
          // temporarily remove audio code for debugging
          // this.setupAudioTracks();

          if (this.currentPart.videoPosition !== 0) {
            this.player.currentTime(this.currentPart.videoPosition);
            this.currentTime = this.currentPart.videoPosition;
          }

          this.loading = false;
        });
      });
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  },
  watch: {
    timeInCourse(value, old) {
      if (this.player) {
        this.$store.dispatch('video/update', {
          timeInCourse: value,
          currentTime: this.player.currentTime(),
        });
      }
    },
    status(value, old) {
      this.$store.commit('video/setItem', { key: 'status', value });
    },
    events(value, old) {
      if (value.length && value.length > 0) {
        for (let i = 0; i < value.length; i++) {
          const event = value[i];
          if (this[event.id] !== undefined) {
            if (typeof this[event.id] === 'function') {
              this[event.id](event.value);
            } else {
              this[event.id] = event.value;
            }
          }
        }
        this.$store.commit('video/clearEvents');
      }
    },
    'currentPart.endOfVideo': {
      handler: function(value, old) {
        if (this.player) {
          this.$store.dispatch('video/update', {
            endOfVideo: value,
          });
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="less">
@green: #67b44b;
@white: #e5e5e5;

.video-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 210px;

  .vjs-big-play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .vjs-text-track-cue > div {
    padding: 0 0.5em;
    background-color: rgba(0, 0, 0, 0.6) !important;
  }

  .vjs-control-bar {
    font-family: bizLibrary-papp-font;
    .vjs-button {
      cursor: pointer;
      &:focus {
        outline: none;
        text-shadow: none;
      }
      &:hover {
        color: @green;
      }
    }
    .vjs-icon-placeholder:before {
      font-family: bizLibrary-papp-font;
      font-size: 18px;
    }
    .vjs-play-control.vjs-paused .vjs-icon-placeholder:before {
      content: '\e267';
    }
    .vjs-play-control.vjs-playing .vjs-icon-placeholder:before {
      content: '\e90a';
    }
    .vjs-seek-button.skip-back.skip-10 .vjs-icon-placeholder:before {
      content: '\e900';
    }
    .vjs-current-time,
    .vjs-time-divider,
    .vjs-duration {
      font-family: Open Sans;
      display: block;
    }
    .vjs-time-divider {
      padding: 0;
      min-width: 1em;
    }
    .vjs-duration {
      padding: 0;
      margin-right: auto;
    }
    .vjs-captions-button {
      .vjs-icon-placeholder:before {
        content: '\e907';
      }
      .vjs-texttrack-settings {
        display: none;
      }
      .vjs-menu li {
        text-transform: capitalize;
      }
    }
    .vjs-audio-button {
      font-family: VideoJS;
      font-weight: normal;
      font-style: normal;
      margin-top: 0.05em;
     .vjs-audio-button:before {
        content: '\f11e';
        font-size: 18px;
      }
      .vjs-menu li {
        text-transform: capitalize;
      }
    }
    .vjs-vol-3 .vjs-icon-placeholder:before {
      content: '\e903';
    }
    .vjs-vol-2 .vjs-icon-placeholder:before {
      content: '\e905';
    }
    .vjs-vol-1 .vjs-icon-placeholder:before {
      content: '\e904';
    }
    .vjs-vol-0 .vjs-icon-placeholder:before {
      content: '\e906';
    }
    .vjs-fullscreen-control .vjs-icon-placeholder:before {
      content: '\e908';
    }
  }

  .vjs-fullscreen .vjs-fullscreen-control .vjs-icon-placeholder:before {
    content: '\e909';
  }

  .vjs-progress-control {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 0;

    &:hover {
      .vjs-mouse-display {
        &:after {
          display: none;
        }
      }
    }

    .vjs-progress-holder {
      margin: 0;
      top: 0;
      height: 0.5em !important;

      .vjs-time-tooltip {
        font-size: 10px;
        font-family: 'Open Sans';
      }

      .vjs-mouse-display {
        .vjs-time-tooltip {
          background-color: rgba(0, 0, 0, 0.5);
          color: @white;
        }
      }

      .vjs-load-progress {
        background-color: @white;
      }

      .vjs-play-progress {
        background-color: @green;
        .vjs-time-tooltip {
          background-color: @green;
        }

        &:before {
          color: @green;
          font-size: 1.2em;
        }
      }
    }
  }
  .vjs-has-started.vjs-user-inactive.vjs-playing {
    .vjs-control-bar {
      visibility: visible;
      opacity: 1;
      background: 0 0;
      -webkit-transition: all 1s;
      -moz-transition: all 1s;
      -o-transition: all 1s;
      transition: all 1s;
      .vjs-control,
      .vjs-time-control {
        visibility: hidden;
        opacity: 0;
        -webkit-transition: all 1s;
        -moz-transition: all 1s;
        -o-transition: all 1s;
        transition: all 1s;
      }
      .vjs-progress-control {
        visibility: visible;
        opacity: 1;
        top: 91%;
        -webkit-transition: all 0.8s !important;
        transition: all 0.8s !important;
        -webkit-transform: translate(0, -100%);
        -ms-transform: translate(0, -100%);
        transform: translate(0, -100%);
        .vjs-play-progress.vjs-slider-bar:before {
          content: '';
        }
      }
    }
  }
}
</style>
